<template>
  <div v-if="isVisible" class="notice-bell">
    <el-popover
      ref="popover"
      v-model="open"
      placement="bottom-end"
      trigger="click"
      :width="isSplit ? 760 : 380"
      popper-class="notice-bell-popover"
      :visible-arrow="false"
    >
      <!-- @click.stop: el-popover menutup diri kalau elemen yang diklik sudah hilang dari DOM (pindah list <-> detail, dismiss) -->
      <!-- layar lebar: daftar di kiri + isi di kanan (seperti new EMM); layar kecil: daftar -> detail -> Back -->
      <div class="notice-bell__panel" :class="{ 'notice-bell__panel--split': isSplit }" @click.stop>
        <div v-if="isSplit || !activeNotice" class="notice-bell__side">
          <div class="notice-bell__header">
            <span class="notice-bell__heading">Notices</span>
            <el-popover v-if="notices.length" trigger="hover" :disabled="!isViewMode" content="Dismiss is not available in view-as mode" placement="top">
              <span slot="reference">
                <button type="button" class="notice-bell__link" :disabled="isViewMode" @click="dismissAll">Dismiss all</button>
              </span>
            </el-popover>
          </div>
          <div class="notice-bell__list">
            <button
              v-for="notice in notices"
              :key="notice.id"
              type="button"
              class="notice-bell__item"
              :class="{ 'is-active': isSplit && activeNotice && activeNotice.id === notice.id }"
              @click="selectedId = notice.id"
            >
              <span class="notice-bell__dot" :class="'notice-bell__dot--' + notice.type"></span>
              <span class="notice-bell__item-text">
                <span class="notice-bell__item-title">{{ notice.title }}</span>
                <span class="notice-bell__meta">{{ typeLabel(notice.type) }} &middot; {{ formatDate(notice.start_date) }}</span>
              </span>
              <i v-if="!isSplit" class="fas fa-chevron-right notice-bell__chevron"></i>
            </button>
            <div v-if="!notices.length" class="notice-bell__empty">No notices</div>
          </div>
        </div>

        <div v-if="activeNotice" class="notice-bell__main">
          <!-- header selalu satu baris: dua kolom = tanggal + badge (sejajar header "Notices"), satu kolom = Back + badge -->
          <div class="notice-bell__header">
            <span v-if="isSplit" class="notice-bell__meta">{{ formatDate(activeNotice.start_date) }}</span>
            <button v-else type="button" class="notice-bell__link" @click="selectedId = null">
              <i class="fas fa-chevron-left"></i> Back
            </button>
            <span class="notice-type-badge" :class="'notice-type-badge--' + activeNotice.type">{{ typeLabel(activeNotice.type) }}</span>
          </div>
          <div class="notice-bell__detail">
            <!-- garis tipis memisahkan judul dari isi, terutama kalau isi diawali heading -->
            <div class="notice-bell__detail-top">
              <div class="notice-bell__detail-title">{{ activeNotice.title }}</div>
              <div v-if="!isSplit" class="notice-bell__meta">{{ formatDate(activeNotice.start_date) }}</div>
            </div>
            <div class="notice-content" v-html="activeNotice.content"></div>
          </div>
          <div class="notice-bell__footer">
            <el-popover trigger="hover" :disabled="!isViewMode" content="Dismiss is not available in view-as mode" placement="top">
              <span slot="reference">
                <button type="button" class="notice-bell__dismiss" :disabled="isViewMode" @click="dismissSelected">Dismiss</button>
              </span>
            </el-popover>
          </div>
        </div>
      </div>

      <button slot="reference" type="button" class="notice-bell__button" aria-label="Notices">
        <i class="far fa-bell"></i>
        <span v-if="notices.length" class="notice-bell__badge">{{ notices.length > 99 ? '99+' : notices.length }}</span>
      </button>
    </el-popover>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { isNoticeAudience, formatNoticeDate, noticeTypeLabel } from '@/helpers/notices';
import './notices.css';

// mulai lebar ini bell memakai dua kolom (daftar + isi) seperti new EMM
const SPLIT_MEDIA_QUERY = '(min-width: 1200px)';

/**
 * TASK-EMM-1020: tampilan bell (Keputusan 3). Dirender dua kali (desktop dan mobile);
 * pengambilan data dan popup Critical ada di NoticeCenter.vue.
 */
export default {
  name: 'NoticeBell',
  data() {
    return {
      open: false,
      selectedId: null,
      isWide: false,
      mediaQuery: null,
    };
  },
  computed: {
    ...mapGetters(['visibleNotices', 'noticesUnavailable']),
    notices() {
      return this.visibleNotices;
    },
    isVisible() {
      return isNoticeAudience(this.$global) && !this.noticesUnavailable;
    },
    isViewMode() {
      return this.$global.globalviewmode === true;
    },
    selectedNotice() {
      if (this.selectedId === null) return null;
      return this.notices.find(notice => notice.id === this.selectedId) || null;
    },
    // dua kolom hanya kalau ada notice; daftar kosong tetap panel sempit
    isSplit() {
      return this.isWide && this.notices.length > 0;
    },
    // dua kolom: notice pertama otomatis terpilih; satu kolom: hanya yang diklik
    activeNotice() {
      if (this.isSplit) return this.selectedNotice || this.notices[0];
      return this.selectedNotice;
    },
  },
  mounted() {
    if (typeof window.matchMedia !== 'function') return;
    this.mediaQuery = window.matchMedia(SPLIT_MEDIA_QUERY);
    this.isWide = this.mediaQuery.matches;
    if (this.mediaQuery.addEventListener) {
      this.mediaQuery.addEventListener('change', this.onMediaChange);
    } else {
      this.mediaQuery.addListener(this.onMediaChange);
    }
  },
  beforeDestroy() {
    if (!this.mediaQuery) return;
    if (this.mediaQuery.removeEventListener) {
      this.mediaQuery.removeEventListener('change', this.onMediaChange);
    } else {
      this.mediaQuery.removeListener(this.onMediaChange);
    }
  },
  watch: {
    open(value) {
      if (!value) {
        this.selectedId = null;
      }
    },
    // lebar panel berubah (380 <-> 760) saat terbuka: posisi dihitung ulang supaya tetap rata kanan dengan bell
    isSplit() {
      if (!this.open) return;
      this.$nextTick(() => {
        const popover = this.$refs.popover;
        if (popover && typeof popover.updatePopper === 'function') {
          popover.updatePopper();
        }
      });
    },
  },
  methods: {
    onMediaChange(event) {
      this.isWide = event.matches;
    },
    typeLabel(type) {
      return noticeTypeLabel(type);
    },
    formatDate(value) {
      return formatNoticeDate(value);
    },
    async dismissSelected() {
      const notice = this.activeNotice;
      if (!notice || this.isViewMode) return;
      // dua kolom: notice berikutnya otomatis terpilih; satu kolom: kembali ke daftar
      this.selectedId = null;
      try {
        await this.$store.dispatch('dismissNotice', notice.id);
      } catch (error) {
        this.handleDismissError();
      }
    },
    async dismissAll() {
      if (this.isViewMode) return;
      try {
        await this.$store.dispatch('dismissAllNotices');
      } catch (error) {
        this.handleDismissError();
      }
    },
    handleDismissError() {
      this.$notify({ type: 'danger', message: 'Failed to dismiss notice. Please try again.', icon: 'fas fa-bug' });
      // kembalikan isi bell sesuai data server
      const user = this.$store.getters.userData;
      this.$store.dispatch('fetchVisibleNotices', {
        viewUserId: this.isViewMode && user ? user.id : null,
      }).catch(() => {});
    },
  },
};
</script>

<style>
.notice-bell {
  display: inline-flex;
  align-items: center;
}
.notice-bell__button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: #fff;
  color: #000;
  font-size: 14px;
  cursor: pointer;
  outline: none;
}
.notice-bell__badge {
  position: absolute;
  top: -6px;
  right: -8px;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: 9px;
  background: #dc2626;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 18px;
  text-align: center;
}

/* emm.css mengubah semua .el-popover (rata tengah, 11px, break-all), jadi dikembalikan di sini.
   Panel selalu putih di kedua tema, sama seperti menu profil, dropdown, dan dialog lain di old EMM. */
.notice-bell-popover.el-popover {
  max-width: calc(100vw - 16px);
  padding: 0;
  text-align: left;
  font-size: 13px;
  word-break: normal;
  color: #222a42;
  background: #fff;
  border-color: #ebeef5;
}
.notice-bell__panel {
  display: flex;
  flex-direction: column;
  max-height: 70vh;
}
.notice-bell__side,
.notice-bell__main {
  display: flex;
  flex-direction: column;
  min-height: 0;
}
/* dua kolom: tinggi tetap supaya daftar dan isi bisa di-scroll masing-masing */
.notice-bell__panel--split {
  flex-direction: row;
  height: 440px;
}
.notice-bell__panel--split .notice-bell__side {
  flex: 0 0 290px;
  border-right: 1px solid #ebeef5;
}
.notice-bell__panel--split .notice-bell__main {
  flex: 1;
  min-width: 0;
}
.notice-bell__panel--split .notice-bell__detail {
  flex: 1;
  padding: 16px 18px;
}
.notice-bell__panel--split .notice-bell__footer {
  margin-top: auto;
}
.notice-bell__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px;
  border-bottom: 1px solid #ebeef5;
}
.notice-bell__heading {
  font-size: 15px;
  font-weight: 600;
}
.notice-bell__link {
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font-size: 12px;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
}
.notice-bell__link:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.notice-bell__list {
  overflow-y: auto;
}
.notice-bell__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 14px;
  border: 0;
  border-bottom: 1px solid #ebeef5;
  background: none;
  color: inherit;
  text-align: left;
  cursor: pointer;
}
/* kotak fokus hitam hanya untuk navigasi keyboard, bukan setelah diklik mouse */
.notice-bell__item:focus:not(:focus-visible) {
  outline: none;
}
.notice-bell__item:hover {
  background: #f5f7fa;
}
.notice-bell__item.is-active {
  background: #f5f7fa;
  box-shadow: inset 3px 0 0 #3b82f6;
}
.notice-bell__dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}
.notice-bell__dot--info { background: #3b82f6; }
.notice-bell__dot--warning { background: #f59e0b; }
.notice-bell__dot--critical { background: #ef4444; }
.notice-bell__item-text {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
}
.notice-bell__item-title {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.notice-bell__meta {
  font-size: 11px;
  opacity: 0.7;
}
.notice-bell__chevron {
  font-size: 11px;
  opacity: 0.5;
}
.notice-bell__empty {
  padding: 24px 14px;
  text-align: center;
  opacity: 0.7;
}
.notice-bell__detail {
  padding: 12px 14px;
  overflow-y: auto;
}
/* judul selalu lebih besar dari Heading 1 di konten (18px) */
.notice-bell__detail-title {
  font-size: 18px;
  font-weight: 700;
  line-height: 1.3;
  overflow-wrap: anywhere;
}
.notice-bell__panel--split .notice-bell__detail-title {
  font-size: 20px;
}
/* garis dilebarkan ke kiri-kanan (setengah padding detail), teks tetap sejajar isi */
.notice-bell__detail-top {
  margin: 0 -7px 12px;
  padding: 0 7px 12px;
  border-bottom: 1px solid #ebeef5;
}
.notice-bell__panel--split .notice-bell__detail-top {
  margin-left: -9px;
  margin-right: -9px;
  padding-left: 9px;
  padding-right: 9px;
}
/* header kiri dan kanan setinggi sama supaya garis bawahnya lurus */
.notice-bell__panel--split .notice-bell__header {
  min-height: 46px;
}
.notice-bell__footer {
  display: flex;
  justify-content: flex-end;
  padding: 10px 14px;
  border-top: 1px solid #ebeef5;
}
/* tombol outline putih seperti new EMM, bukan tombol hitam penuh */
.notice-bell__dismiss {
  padding: 6px 14px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  background: #fff;
  color: #222a42;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.notice-bell__dismiss:hover {
  background: #f5f7fa;
  border-color: #c0c4cc;
}
.notice-bell__dismiss:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
